import { cloneElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";

import { boldString } from "../Utils";
import { EDITABLE_LAYERS } from "../tools/useLayout";
import SaveSplitButton from "./SaveSplitButton";


export default function DialogEdit({ open, onClose, children, data, modifiedLayout, colissionLayers, editAnnotation, layer }) {
    const { t } = useTranslation();
    const [conflictDialog, setConflictDialog] = useState(false);
    const [existingAnnotation, setExistingAnnotation] = useState(null);
    const [isSaveDisabled, setIsSaveDisabled] = useState(false);
    const childrenRef = useRef(null);
    const resolveConflictRef = useRef(null);
    const TEXTS = useMemo(() => {
        return {
            [EDITABLE_LAYERS.CAPTURE]: { title: t('overseer_app.layouts.edition.edit_aisle', 'Edit aisle') },
            [EDITABLE_LAYERS.ZONES]: { title: t('overseer_app.layouts.edition.edit_zone', 'Edit zone') },
            [EDITABLE_LAYERS.AISLE_SIDE]: { title: t('overseer_app.layouts.edition.edit_linear', 'Edit Linear') },
            [EDITABLE_LAYERS.EXCLUSION_ZONES]: { title: t('overseer_app.layouts.edition.edit_exclusion_zone', 'Edit exclusion zone') },
        }
    }, [t]);

    // The dialog stays mounted between forms, so a form's disabled state must not carry over to the next one.
    useEffect(() => {
        if (!open) setIsSaveDisabled(false);
    }, [open]);

    const onConfirmConflict = useCallback(() => {
        setConflictDialog(false);
        resolveConflictRef.current(true);
        return true;
    }, []);

    const onCancelConflict = useCallback(() => {
        setConflictDialog(false);
        resolveConflictRef.current(false);
    }, []);

    //  Main save handler for aisle editing with children update support
    const onSave = useCallback(async (mustUpdateChildren = false) => {
        if (childrenRef?.current) {
            // Execute child's save callback with updateChildren flag
            const result = childrenRef.current.callbackOnSave(mustUpdateChildren);
            if (!result) return;
            const { needConfirm, callback, existingAnnotation: __existingAnnotation } = result;

            if (needConfirm) {
                setExistingAnnotation(__existingAnnotation);
                setConflictDialog(true);
                const solvedConfirm = await new Promise(resolve => resolveConflictRef.current = resolve);
                if (!solvedConfirm) return;
            }
            callback();
            onClose();
        }
    }, [onClose, childrenRef]);

    const isUniqueName = useCallback((name, layer) => {
        const layerData = modifiedLayout?.[layer] || {};
        return !Object.keys(layerData)?.some(layer => layer?.toLowerCase() === name?.toLowerCase());
    }, [modifiedLayout]);

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle>
                    {TEXTS?.[layer]?.title || t('overseer_app.layouts.edition.edit_annotation', 'Edit annotation')}
                </DialogTitle>
                <DialogContent>
                    {children && cloneElement(children, {
                        ref: childrenRef,
                        data,
                        modifiedLayout,
                        colissionLayers,
                        isUniqueName,
                        editAnnotation,
                        layer,
                        onDisabledChange: setIsSaveDisabled,
                    })}
                </DialogContent>

                <DialogActions>
                    <Button color="secondary" onClick={onClose}>{t('overseer_app.layouts.cancel', 'Cancel')}</Button>
                    {layer === EDITABLE_LAYERS.CAPTURE ? (
                        <div style={{ width: 'auto', minWidth: '130px' }}>
                            <SaveSplitButton
                                onSave={() => onSave(false)} // Parent only
                                onSaveAndChangeLineals={() => onSave(true)} // Parent + children
                                disabled={isSaveDisabled}
                            />
                        </div>
                    ) : (
                        // For other layers: Simple save button
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => onSave(false)}
                            disabled={isSaveDisabled}
                        >
                            {t('overseer_app.layouts.save', 'Save')}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
            <Dialog open={conflictDialog} onClose={() => setConflictDialog(false)} aria-hidden={!conflictDialog}>
                <DialogTitle>
                    {t('overseer_app.layouts.edition.conflict_title', 'Conflict detected')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {boldString(t('overseer_app.layouts.edition.conflict_text', 'There is already an annotation with the same name in this layer (**{{item}}**). A **warning** will be assigned to the existing layer with this name, and it will need to be **reviewed**.', { item: existingAnnotation }))}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onCancelConflict} color="secondary">
                        {t('overseer_app.layouts.cancel', 'Cancel')}
                    </Button>
                    <Button onClick={onConfirmConflict} variant="contained" color="primary">
                        {t('overseer_app.layouts.confirm', 'Confirm')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}